import { Component, Inject } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
// eslint-disable-next-line import/order
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { State, Store } from '@ngrx/store';
import {
  DateFormatConstants,
  isNullOrUndefined,
  ReservationStatusNameConstants,
  ReservationStatusOrder,
  RideRequestTypeConstants
} from '@uzurv/core';
import moment from 'moment-timezone';
import { UpdateReservationStatus } from '../../../store/trip-detail/trip-detail.actions';
import { TripDetailState } from '../../../store/trip-detail/trip-detail.reducer';
import { ReservationListItemVm } from '../../reservation/reservation-list-item-vm';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

export interface AdjustReservationStatusDialogData {
  reservation: ReservationListItemVm;
}

@Component({
  selector: 'app-adjust-reservation-status-dialog',
  templateUrl: './adjust-reservation-status-dialog.component.html',
  styleUrls: ['./adjust-reservation-status-dialog.component.scss']
})
export class AdjustReservationStatusDialogComponent {
  reservation: ReservationListItemVm;
  adjustReservationForm: UntypedFormGroup = this.formBuilder.group({
    timestampTime: [null, Validators.compose([Validators.required, this.validateTimestamp.bind(this)])],
    timestampDate: [new Date(), Validators.required]
  });

  minDate = new Date();
  maxDate = new Date();

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: AdjustReservationStatusDialogData,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AdjustReservationStatusDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    private store$: Store<State<TripDetailState>>
  ) {
    if (!isNullOrUndefined(this.data)) {
      this.reservation = this.data.reservation;

      if (!isNullOrUndefined(this.reservation)) {
        this.minDate = moment
          .tz(this.reservation.rideRequestDetail.itineraryPickupDate, this.reservation.rideRequestDetail.timeZone)
          .toDate();
        this.maxDate = moment.tz(new Date(), this.reservation.rideRequestDetail.timeZone).toDate();

        let patchTimestampTime: string;

        if (
          this.reservation.status === ReservationStatusNameConstants.DRIVER_EN_ROUTE &&
          this.reservation.rideRequestDetail.rideRequestType.code === RideRequestTypeConstants.SCHEDULED
        ) {
          patchTimestampTime = moment
            .tz(this.reservation.rideRequestDetail.pickupDate, this.reservation.rideRequestDetail.timeZone)
            .format(DateFormatConstants.TIME_INPUT_VALUE);
        } else {
          patchTimestampTime = moment
            .tz(new Date(), this.reservation.rideRequestDetail.timeZone)
            .format(DateFormatConstants.TIME_INPUT_VALUE);
        }

        this.adjustReservationForm.patchValue({
          timestampTime: patchTimestampTime,
          timestampDate: moment.tz(new Date(), this.reservation.rideRequestDetail.timeZone).toDate()
        });
      }
    }
  }

  get abbreviatedTimezone(): string {
    if (isNullOrUndefined(this.reservation)) {
      return null;
    }

    return moment().tz(this.reservation.rideRequestDetail.timeZone).zoneAbbr();
  }

  get nextStatus(): string {
    if (isNullOrUndefined(this.reservation)) {
      return '';
    }

    switch (this.reservation.status) {
      case ReservationStatusNameConstants.SCHEDULED:
        return ReservationStatusNameConstants.DRIVER_EN_ROUTE;
      case ReservationStatusNameConstants.DRIVER_EN_ROUTE:
        return ReservationStatusNameConstants.PICK_UP_ARRIVED;
      case ReservationStatusNameConstants.PICK_UP_ARRIVED:
        return ReservationStatusNameConstants.IN_PROGRESS;
      case ReservationStatusNameConstants.IN_PROGRESS:
        return ReservationStatusNameConstants.DROP_OFF_ARRIVED;
      case ReservationStatusNameConstants.DROP_OFF_ARRIVED:
        return ReservationStatusNameConstants.COMPLETED;
      default:
        return null;
    }
  }

  getErrors(errors: object): Array<string> {
    let errorMessages: Array<string> = [];
    Object.keys(errors).forEach(key => {
      if (key === 'dateIsFuture') {
        errorMessages.push('The new status time cannot be in the future.');
      }

      if (key === 'dateIsTooFarInPast') {
        errorMessages.push('The new status time must be less than 20 minutes before the pick-up time.');
      }

      if (key === 'dateIsTooFarBeforeActivatedTime') {
        errorMessages.push('The new status time must be less than 20 minutes before the activation time.');
      }

      if (key === 'timestampIsBeforePreviousStatusTime') {
        errorMessages.push('The new status time must occur after the previous status time.');
      }
    });

    return errorMessages;
  }

  isDatePickerDisabled(): boolean {
    return moment(this.minDate).isSame(this.maxDate, 'day') && !isNullOrUndefined(this.adjustReservationForm.get('timestampDate').value);
  }

  isSubmitDisabled(): boolean {
    if (isNullOrUndefined(this.reservation)) {
      return true;
    }

    if (this.adjustReservationForm.invalid) {
      return true;
    }

    if (isNullOrUndefined(this.nextStatus)) {
      return true;
    }

    return false;
  }

  closeModal() {
    this.dialogRef.close();
  }

  updateReservationStatus() {
    if (this.adjustReservationForm.invalid) {
      return;
    }

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Are you sure?',
          message: 'Are you sure you want to update this reservation status?',
          confirmText: 'Yes, Update'
        }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          const timestamp: Date = this.buildTimestamp();

          this.store$.dispatch(
            UpdateReservationStatus({ payload: { reservationId: this.reservation.id, statusName: this.nextStatus, timestamp: timestamp } })
          );

          this.closeModal();
        }
      });
  }

  buildTimestamp(): Date {
    const timestampMoment = moment.tz(
      this.adjustReservationForm.get('timestampTime').value,
      DateFormatConstants.TIME_INPUT_VALUE,
      this.reservation.rideRequestDetail.timeZone
    );

    if (isNaN(timestampMoment)) {
      return null;
    }

    const timestamp = timestampMoment.toDate();

    const timestampDate: Date = moment
      .tz(this.adjustReservationForm.get('timestampDate').value, this.reservation.rideRequestDetail.timeZone)
      .toDate();

    timestamp.setFullYear(timestampDate.getFullYear());
    timestamp.setMonth(timestampDate.getMonth());
    timestamp.setDate(timestampDate.getDate());

    return timestamp;
  }

  private validateTimestamp(control: AbstractControl): { [key: string]: boolean } {
    const validationErrors: { [key: string]: boolean } = {};

    if (isNullOrUndefined(control.value)) {
      return validationErrors;
    }

    if (isNullOrUndefined(this.reservation)) {
      return validationErrors;
    }

    const timestamp: Date = this.buildTimestamp();

    if (!isNullOrUndefined(timestamp)) {
      if (moment(timestamp).isAfter(moment.tz(new Date(), this.reservation.rideRequestDetail.timeZone))) {
        validationErrors['dateIsFuture'] = true;
      }

      if (
        moment(timestamp).isBefore(
          moment.tz(this.reservation.rideRequestDetail.pickupDate, this.reservation.rideRequestDetail.timeZone).subtract(20, 'minutes')
        )
      ) {
        validationErrors['dateIsTooFarInPast'] = true;
      }

      if (
        this.reservation.rideRequestDetail.rideRequestType.code !== RideRequestTypeConstants.SCHEDULED &&
        moment(timestamp).isBefore(
          moment.tz(this.reservation.rideRequestDetail.activatedAt, this.reservation.rideRequestDetail.timeZone).subtract(20, 'minutes')
        )
      ) {
        validationErrors['dateIsTooFarBeforeActivatedTime'] = true;
      }

      if (this.timestampIsBeforePreviousStatusTime(timestamp)) {
        validationErrors['timestampIsBeforePreviousStatusTime'] = true;
      }
    }

    return validationErrors;
  }

  private timestampIsBeforePreviousStatusTime(timestamp: Date): boolean {
    if (this.reservation.status === ReservationStatusNameConstants.DRIVER_EN_ROUTE) {
      return moment(timestamp).isBefore(moment.tz(this.reservation.driverEnRouteDate, this.reservation.rideRequestDetail.timeZone));
    }

    if (this.reservation.status === ReservationStatusNameConstants.PICK_UP_ARRIVED) {
      return moment(timestamp).isBefore(moment.tz(this.reservation.pickUpArrivedDate, this.reservation.rideRequestDetail.timeZone));
    }

    if (this.reservation.status === ReservationStatusNameConstants.IN_PROGRESS) {
      return moment(timestamp).isBefore(moment.tz(this.reservation.inProgressDate, this.reservation.rideRequestDetail.timeZone));
    }

    if (this.reservation.status === ReservationStatusNameConstants.DROP_OFF_ARRIVED) {
      return moment(timestamp).isBefore(moment.tz(this.reservation.dropOffArrivedDate, this.reservation.rideRequestDetail.timeZone));
    }

    return false;
  }
}
